import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Search, Download, Loader2, ExternalLink, FileText, Filter,
  Building2, CalendarDays, BookOpenText, ChevronDown, ChevronUp, Inbox,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_doe-scraper-sp/artifacts/zc2iv8gg_WhatsApp%20Image%202026-08-31%20at%2015.53.19%20%281%29.jpeg";

const TYPE_STYLES = {
  "Edital": "bg-[#1f7a3d] text-white",
  "Atribuição": "bg-[#0d6e6e] text-white",
  "Portaria": "bg-[#5b47b0] text-white",
  "Comunicado": "bg-[#f2911b] text-white",
  "Concurso": "bg-[#b0231a] text-white",
  "Processo Seletivo": "bg-[#c26a10] text-white",
  "Extrato/Contrato": "bg-[#5c6b3a] text-white",
  "Dispensa": "bg-[#8a5a2b] text-white",
  "Resultado": "bg-[#2b6ca3] text-white",
  "Outros": "bg-[#6b6b6b] text-white",
};

function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function TypeBadge({ t }) {
  return (
    <span data-testid={`type-badge-${t}`} className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${TYPE_STYLES[t] || TYPE_STYLES["Outros"]}`}>
      {t}
    </span>
  );
}

function Row({ p, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`result-row-${idx}`} className="rise border-b border-[rgba(31,122,61,0.12)] last:border-0">
      <div className="grid grid-cols-12 gap-3 px-4 py-3 items-start hover:bg-[rgba(31,122,61,0.04)] transition-colors">
        <div className="col-span-12 md:col-span-3 flex items-start gap-2">
          <Building2 className="w-4 h-4 mt-0.5 text-[#1f7a3d] shrink-0" />
          <div className="leading-tight">
            <div className="inline-block px-2 py-0.5 rounded-md bg-[#1f7a3d] text-white text-[12px] font-bold" data-testid={`region-${idx}`}>{p.region || p.regional_unit}</div>
            <div className="text-[11px] text-[#7a7f70] mt-1">{p.regional_unit}</div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-6">
          <p className="text-sm text-[#23281f] leading-snug">{p.title}</p>
          {p.summary && (
            <button data-testid={`toggle-summary-${idx}`} onClick={() => setOpen(!open)} className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-[#b0231a] hover:underline">
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {open ? "Ocultar resumo" : "Ver resumo"}
            </button>
          )}
          {open && <p className="mt-1 text-[13px] text-[#4a5142] bg-[#f6f3ea] rounded-md p-2 leading-relaxed">{p.summary}</p>}
        </div>
        <div className="col-span-6 md:col-span-2"><TypeBadge t={p.type} /><div className="text-[11px] text-[#7a7f70] mt-1">{p.date}</div></div>
        <div className="col-span-6 md:col-span-1 flex md:justify-end">
          <a data-testid={`open-link-${idx}`} href={p.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1f7a3d] hover:text-[#f2911b] transition-colors">
            Abrir <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [date, setDate] = useState(todayISO());
  const scope = "regionais";
  const [allTypes, setAllTypes] = useState([]);
  const [selTypes, setSelTypes] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState(null);
  const [liveFilter, setLiveFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  useEffect(() => {
    axios.get(`${API}/types`).then((r) => setAllTypes(r.data.types || [])).catch(() => {});
  }, []);

  const toggleType = (t) =>
    setSelTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const runSearch = async () => {
    setLoading(true);
    setResults(null);
    setCityFilter("");
    setLiveFilter("");
    try {
      const r = await axios.post(`${API}/search`, {
        date,
        types: selTypes.length ? selTypes : null,
        keyword: keyword.trim(),
        scope,
      });
      setResults(r.data);
      if (r.data.count === 0) toast.info("Nenhuma publicação encontrada para os filtros selecionados.");
      else toast.success(`${r.data.count} publicação(ões) encontrada(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar publicações.");
    } finally {
      setLoading(false);
    }
  };

  const cities = useMemo(() => {
    if (!results) return [];
    return Array.from(new Set(results.publications.map((p) => p.region).filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [results]);

  const visible = useMemo(() => {
    if (!results) return [];
    let list = results.publications;
    if (cityFilter) list = list.filter((p) => p.region === cityFilter);
    const kw = liveFilter.trim().toLowerCase();
    if (kw) list = list.filter((p) => (p.title + " " + p.regional_unit + " " + p.summary).toLowerCase().includes(kw));
    return list;
  }, [results, liveFilter, cityFilter]);

  const downloadExcel = async () => {
    if (!visible.length) return;
    setExporting(true);
    try {
      const r = await axios.post(`${API}/export`, { date, publications: visible }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `DOE-SP_Educacao_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel gerado com sucesso!");
    } catch (e) {
      toast.error("Erro ao gerar o Excel.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="relative overflow-hidden bg-[#fffdf8] border-b-4 border-[#1f7a3d]">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#1f7a3d] via-[#f2911b] to-[#b0231a]" />
        <div className="max-w-6xl mx-auto px-5 py-6 flex items-center gap-4">
          <img src={LOGO} alt="Casa dos Professores" className="h-16 w-auto object-contain drop-shadow-sm" data-testid="brand-logo" />
          <div>
            <h1 className="text-2xl md:text-[28px] leading-none font-700 text-[#14562b] font-semibold">Casa dos Professores</h1>
            <p className="text-[13px] md:text-sm text-[#7a7f70] mt-1">
              Busca no Diário Oficial de SP · Caderno Executivo · Atos de Gestão e Despesas · Secretaria da Educação
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-7">
        {/* Search panel */}
        <section className="card-paper rounded-2xl p-5 md:p-6 rise">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="flex items-center gap-1.5 text-[12px] font-semibold text-[#4a5142] mb-1.5"><CalendarDays className="w-4 h-4 text-[#1f7a3d]" /> Data da publicação</label>
              <input data-testid="date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-lg border border-[rgba(31,122,61,0.3)] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1f7a3d]" />
            </div>
            <div className="md:col-span-8">
              <label className="flex items-center gap-1.5 text-[12px] font-semibold text-[#4a5142] mb-1.5"><Search className="w-4 h-4 text-[#1f7a3d]" /> Palavra-chave (opcional)</label>
              <input data-testid="keyword-input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="ex.: diretor, PEI, cadastro emergencial..."
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                className="w-full rounded-lg border border-[rgba(31,122,61,0.3)] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1f7a3d]" />
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 text-[12px] text-[#4a5142]">
            <Building2 className="w-4 h-4 text-[#1f7a3d]" />
            <span className="font-semibold">Estrutura:</span> Secretaria da Educação · <span className="font-semibold text-[#14562b]">Unidades Regionais de Ensino (Diretorias de Ensino)</span>
          </div>

          {/* Type chips */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#4a5142] mb-2"><Filter className="w-4 h-4 text-[#1f7a3d]" /> Tipos de ato <span className="font-normal text-[#9a9f90]">(nenhum selecionado = todos)</span></div>
            <div className="flex flex-wrap gap-2">
              {allTypes.map((t) => {
                const active = selTypes.includes(t);
                return (
                  <button key={t} data-testid={`type-filter-${t}`} onClick={() => toggleType(t)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${active ? "bg-[#1f7a3d] text-white border-[#1f7a3d] shadow-sm" : "bg-white text-[#4a5142] border-[rgba(31,122,61,0.3)] hover:border-[#1f7a3d]"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="search-button" onClick={runSearch} disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-[#1f7a3d] hover:bg-[#14562b] text-white font-semibold text-sm px-6 py-3 transition-colors disabled:opacity-60 shadow-md">
              {loading ? <Loader2 className="w-4 h-4 spin-slow" /> : <Search className="w-4 h-4" />}
              {loading ? "Buscando..." : "Buscar publicações"}
            </button>
            <button data-testid="export-button" onClick={downloadExcel} disabled={!visible.length || exporting}
              className="inline-flex items-center gap-2 rounded-full bg-[#f2911b] hover:bg-[#d97e0c] text-white font-semibold text-sm px-6 py-3 transition-colors disabled:opacity-40 shadow-md">
              {exporting ? <Loader2 className="w-4 h-4 spin-slow" /> : <Download className="w-4 h-4" />}
              Baixar Excel
            </button>
          </div>
        </section>

        {/* Results */}
        <section className="mt-6">
          {loading && (
            <div className="card-paper rounded-2xl p-12 flex flex-col items-center justify-center text-[#7a7f70] gap-3">
              <Loader2 className="w-8 h-8 spin-slow text-[#1f7a3d]" />
              <p className="text-sm">Consultando o Diário Oficial e coletando publicações...</p>
            </div>
          )}

          {!loading && results && (
            <div className="card-paper rounded-2xl overflow-hidden rise">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-[#f6f3ea] border-b border-[rgba(31,122,61,0.14)]">
                <div className="flex items-center gap-2 text-[#14562b]">
                  <BookOpenText className="w-5 h-5" />
                  <span className="font-semibold" data-testid="results-count">
                    {visible.length} de {results.count} publicação(ões) · {results.date}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid="city-filter" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                    className="py-2 px-3 rounded-full border border-[rgba(31,122,61,0.3)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1f7a3d] max-w-[220px]">
                    <option value="">Todas as cidades ({cities.length})</option>
                    {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9a9f90]" />
                    <input data-testid="live-filter-input" value={liveFilter} onChange={(e) => setLiveFilter(e.target.value)} placeholder="Filtrar resultados..."
                      className="pl-9 pr-3 py-2 rounded-full border border-[rgba(31,122,61,0.3)] bg-white text-sm w-52 focus:outline-none focus:ring-2 focus:ring-[#1f7a3d]" />
                  </div>
                </div>
              </div>

              {visible.length === 0 ? (
                <div className="p-12 flex flex-col items-center text-[#9a9f90] gap-2">
                  <Inbox className="w-10 h-10" />
                  <p className="text-sm">Nenhuma publicação para exibir.</p>
                </div>
              ) : (
                <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#9a9f90] border-b border-[rgba(31,122,61,0.1)]">
                  <div className="col-span-3">Cidade / Região</div>
                  <div className="col-span-6">Título</div>
                  <div className="col-span-2">Tipo / Data</div>
                  <div className="col-span-1 text-right">Link</div>
                </div>
              )}
              <div>
                {visible.map((p, i) => <Row key={p.id + i} p={p} idx={i} />)}
              </div>
            </div>
          )}

          {!loading && !results && (
            <div className="card-paper rounded-2xl p-12 flex flex-col items-center text-center gap-3 rise">
              <FileText className="w-12 h-12 text-[#1f7a3d]" />
              <h2 className="text-xl text-[#14562b] font-semibold">Escolha uma data e clique em Buscar</h2>
              <p className="text-sm text-[#7a7f70] max-w-md">
                O sistema consulta as publicações das Unidades Regionais de Ensino (Diretorias de Ensino) no Diário Oficial do Estado de São Paulo e permite exportar tudo para Excel.
              </p>
            </div>
          )}
        </section>

        <footer className="mt-10 pb-8 text-center text-[12px] text-[#9a9f90]">
          Fonte: Diário Oficial do Estado de São Paulo · doe.sp.gov.br — dados públicos oficiais.
        </footer>
      </main>
    </div>
  );
}

export default App;
